import React, { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Check, Copy, Maximize2, RefreshCw, Sparkles, Square } from 'lucide-react';
import { Button, Dialog, DialogBody, DialogHeader, IconButton, Spinner } from '../../../components/ui';
import { userStorage } from '../../../utils/auth';
import { briefIntelligence } from '../intelligenceApi';
import { BriefRequest, IntelligenceDomain, ReferencesResult } from '../types';
import { StreamStatus, useIntelligenceStream } from '../useIntelligenceStream';
import { IntelText } from './IntelText';

/* ------------------------------------------------------------------ */
/* Shared AI helpers (also used by AskPanel and the workspaces)        */
/* ------------------------------------------------------------------ */

/** Role of the signed-in user, read without requiring the auth provider */
export function currentUserRole(): string {
  return String(userStorage.get()?.role || '').toLowerCase();
}

export const isAdminRole = (role = currentUserRole()) => role === 'admin' || role === 'super_admin';

/** discover / assess-influence are limited to these roles on the server */
export const canResearch = (role = currentUserRole()) => isAdminRole(role) || role === 'top_owner';

/** One-line reason when an AI call cannot run */
export function aiFailureReason(status: StreamStatus, error: string | null): string {
  // 404 from /api/intelligence: the backend predates these endpoints; settings can't fix that
  if (status === 'unavailable') {
    return isAdminRole()
      ? 'AI isn’t available on this server yet — the backend needs to be redeployed'
      : 'AI isn’t available on this server yet';
  }
  const message = (error || '').trim();
  // A key that exists but is refused is a different fix from no key at all
  if (/rejected the api key|invalid authentication|invalid api key|unauthorized/i.test(message))
    return 'The AI provider rejected its API key';
  if (/api key|not configured|no provider|provider.*(missing|disabled)/i.test(message)) return 'No AI provider configured';
  if (/^(network error|failed to fetch|load failed|networkerror)/i.test(message)) return 'Connection lost — try again';
  if (/quota|rate.?limit|429/i.test(message)) return 'The AI provider is rate-limited — try again shortly';
  return message || 'Something went wrong';
}

/** Quiet notice for unavailable or failed AI calls, with a settings link for admins */
export const AiNotice: React.FC<{
  status: StreamStatus;
  error: string | null;
  onRetry?: () => void;
  className?: string;
}> = ({ status, error, onRetry, className = '' }) => {
  const reason = aiFailureReason(status, error);
  const needsSettings = reason === 'No AI provider configured' || reason === 'The AI provider rejected its API key';
  const quiet = status === 'unavailable' || needsSettings;
  return (
    <div
      role={quiet ? 'status' : 'alert'}
      className={`flex flex-wrap items-center gap-x-2 gap-y-1 rounded-xl px-3 py-2 text-[12.5px] ${
        quiet ? 'bg-neutral-50 text-neutral-600' : 'bg-primary-50 text-primary-700'
      } ${className}`}
    >
      <span className="min-w-0">{reason}</span>
      <span className="ml-auto flex items-center gap-2">
        {isAdminRole() && needsSettings && (
          <Link to="/ai-settings" className="text-[12px] text-ink underline decoration-neutral-300 underline-offset-2 hover:decoration-ink">
            AI settings
          </Link>
        )}
        {onRetry && status === 'error' && !quiet && (
          <button type="button" onClick={onRetry} className="text-[12px] underline decoration-current/30 underline-offset-2 hover:decoration-current">
            Retry
          </button>
        )}
      </span>
    </div>
  );
};

/** Streamed progress lines: finished steps get a check, the current one a spinner */
export const StreamProgress: React.FC<{ lines: string[]; running: boolean; max?: number; className?: string }> = ({
  lines,
  running,
  max = 4,
  className = '',
}) => {
  const shown = lines.slice(-max);
  const offset = lines.length - shown.length;
  if (!shown.length && !running) return null;
  return (
    <ol className={`space-y-1 ${className}`} aria-live="polite">
      {(shown.length ? shown : ['Working']).map((line, i) => {
        const current = running && i === Math.max(0, shown.length - 1);
        return (
          <li
            key={`${offset + i}-${line}`}
            className={`flex animate-fade-up items-center gap-2 text-[12.5px] motion-reduce:animate-none ${current ? 'text-ink' : 'text-neutral-400'}`}
          >
            <span className="inline-flex h-3.5 w-3.5 flex-shrink-0 items-center justify-center">
              {current ? <Spinner className="h-3 w-3 text-primary-600" /> : <Check className="h-3 w-3" strokeWidth={2} />}
            </span>
            <span className="min-w-0 truncate">{line}</span>
          </li>
        );
      })}
    </ol>
  );
};

export function relativeTime(at: number, now = Date.now()): string {
  const s = Math.max(0, Math.round((now - at) / 1000));
  if (s < 45) return 'just now';
  const m = Math.round(s / 60);
  if (m < 60) return `${m} min ago`;
  const h = Math.round(m / 60);
  if (h < 24) return `${h} h ago`;
  return new Date(at).toLocaleDateString();
}

export function useCopy(timeout = 1600) {
  const [copied, setCopied] = useState(false);
  const copy = useCallback(
    async (text: string) => {
      try {
        await navigator.clipboard?.writeText(text);
        setCopied(true);
        window.setTimeout(() => setCopied(false), timeout);
      } catch {
        // clipboard unavailable
      }
    },
    [timeout],
  );
  return { copied, copy };
}

/** AI text with entity tokens turned back into plain names, for copying */
export const plainText = (text: string) => text.replace(/\[\[entity:[0-9a-fA-F-]{8,}\|([^\]]+)\]\]/g, '$1');

/* ------------------------------------------------------------------ */
/* BriefPanel                                                          */
/* ------------------------------------------------------------------ */

export interface BriefPanelProps {
  domain: IntelligenceDomain;
  /** Client-computed analytics digest sent as context (≤ 12,000 chars) */
  buildContext: () => string;
  /** Entities in scope (selection, cluster…); empty = the whole view */
  focusIds?: string[];
  onEntity?: (id: string) => void;
  onEntityHover?: (id: string | null) => void;
  entityColor?: (id: string) => string | undefined;
  /** Entities referenced by a freshly generated assessment */
  onReferences?: (ids: string[]) => void;
  title?: string;
  /** e.g. "Whole network" or "Amara Diallo" */
  scopeLabel?: string;
  /** Changes when the scope changes, so an older assessment is marked as such */
  scopeKey?: string;
  className?: string;
}

interface BriefMemo {
  text: string;
  entityIds: string[];
  at: number;
  scopeKey?: string;
  scopeLabel?: string;
}

/** Last assessment per domain, kept for the session so panels can unmount freely */
const briefMemory = new Map<IntelligenceDomain, BriefMemo>();

/** Test helper */
export const __resetBriefMemory = () => briefMemory.clear();

/**
 * AI assessment block: generate → streamed progress and text → entity chips that
 * highlight (hover) and select (click) entities. The last result survives unmounts.
 */
export const BriefPanel: React.FC<BriefPanelProps> = ({
  domain,
  buildContext,
  focusIds,
  onEntity,
  onEntityHover,
  entityColor,
  onReferences,
  title = 'Assessment',
  scopeLabel,
  scopeKey,
  className = '',
}) => {
  const stream = useIntelligenceStream<BriefRequest, ReferencesResult>(briefIntelligence);
  const [memo, setMemo] = useState<BriefMemo | undefined>(() => briefMemory.get(domain));
  const [expanded, setExpanded] = useState(false);
  const { copied, copy } = useCopy();
  const titleId = useId();
  const scopeAtStart = useRef<{ key?: string; label?: string }>({});
  const onReferencesRef = useRef(onReferences);
  onReferencesRef.current = onReferences;

  const running = stream.status === 'running';
  const failed = stream.status === 'error' || stream.status === 'unavailable';

  const generate = useCallback(() => {
    scopeAtStart.current = { key: scopeKey, label: scopeLabel };
    stream.start({ domain, focusIds: focusIds?.length ? focusIds : undefined, context: buildContext() });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [domain, focusIds, buildContext, scopeKey, scopeLabel, stream.start]);

  // Save a finished assessment for the session
  useEffect(() => {
    if (stream.status !== 'done' || !stream.text.trim()) return;
    const entityIds = stream.result?.entityIds?.length ? stream.result.entityIds : stream.entityIds;
    const next: BriefMemo = {
      text: stream.text,
      entityIds,
      at: Date.now(),
      scopeKey: scopeAtStart.current.key,
      scopeLabel: scopeAtStart.current.label,
    };
    briefMemory.set(domain, next);
    setMemo(next);
    onReferencesRef.current?.(entityIds);
  }, [stream.status, stream.text, stream.result, stream.entityIds, domain]);

  // Referenced entities appear on the canvas while the text streams
  const streamedIds = stream.entityIds.join(',');
  useEffect(() => {
    if (running && stream.entityIds.length) onReferencesRef.current?.(stream.entityIds);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [running, streamedIds]);

  const showStream = running || (stream.status === 'done' && !!stream.text) || (failed && !!stream.text);
  const text = showStream ? stream.text : memo?.text || '';
  const stale = !running && !!memo && !showStream && scopeKey !== undefined && memo.scopeKey !== scopeKey;
  const hasText = !!text.trim();

  const body = (inDialog: boolean) => (
    <IntelText
      text={text}
      streaming={running}
      onEntity={(id) => {
        if (inDialog) setExpanded(false);
        onEntity?.(id);
      }}
      onEntityHover={onEntityHover}
      entityColor={entityColor}
    />
  );

  return (
    <section aria-label={title} className={`min-w-0 ${className}`}>
      {!hasText && !running && (
        <div className="flex flex-col items-start gap-2">
          <Button size="sm" variant="secondary" onClick={generate} disabled={running}>
            <Sparkles className="h-3.5 w-3.5 text-primary-600" strokeWidth={1.75} />
            Generate assessment
          </Button>
          {scopeLabel && <p className="text-[12px] text-neutral-400">Scope · {scopeLabel}</p>}
        </div>
      )}

      {running && !stream.text && <StreamProgress lines={stream.progress} running className="mt-1" />}

      {hasText && (
        <>
          <div className="mb-2 flex min-h-[28px] items-center gap-1">
            <p className="min-w-0 flex-1 truncate text-[12px] text-neutral-400">
              {running ? (
                <span className="inline-flex items-center gap-1.5 text-neutral-500">
                  <Spinner className="h-3 w-3 text-primary-600" />
                  {stream.progress[stream.progress.length - 1] || 'Writing'}
                </span>
              ) : showStream ? (
                `Just now${scopeAtStart.current.label ? ` · ${scopeAtStart.current.label}` : ''}`
              ) : (
                `${memo ? relativeTime(memo.at) : ''}${memo?.scopeLabel ? ` · ${memo.scopeLabel}` : ''}`
              )}
            </p>
            {running ? (
              <IconButton label="Stop" size="xs" onClick={stream.stop}>
                <Square className="h-3 w-3 fill-current" strokeWidth={1.75} />
              </IconButton>
            ) : (
              <>
                <IconButton label={copied ? 'Copied' : 'Copy'} size="xs" onClick={() => copy(plainText(text))}>
                  {copied ? <Check className="h-3.5 w-3.5" strokeWidth={2} /> : <Copy className="h-3.5 w-3.5" strokeWidth={1.75} />}
                </IconButton>
                <IconButton label="Read in full view" size="xs" onClick={() => setExpanded(true)}>
                  <Maximize2 className="h-3.5 w-3.5" strokeWidth={1.75} />
                </IconButton>
                <IconButton label="Regenerate" size="xs" onClick={generate}>
                  <RefreshCw className="h-3.5 w-3.5" strokeWidth={1.75} />
                </IconButton>
              </>
            )}
          </div>
          {stale && (
            <button
              type="button"
              onClick={generate}
              className="mb-2 flex w-full items-center gap-2 rounded-xl bg-neutral-50 px-3 py-1.5 text-left text-[12px] text-neutral-500 transition-colors hover:bg-neutral-100 hover:text-ink"
            >
              <RefreshCw className="h-3 w-3 flex-shrink-0" strokeWidth={1.75} />
              <span className="min-w-0 truncate">View changed{scopeLabel ? ` — update for ${scopeLabel}` : ' — update'}</span>
            </button>
          )}
          {body(false)}
        </>
      )}

      {failed && <AiNotice status={stream.status} error={stream.error} onRetry={generate} className="mt-2" />}

      <Dialog open={expanded} onClose={() => setExpanded(false)} labelledBy={titleId} handle className="max-h-[88dvh] sm:max-w-2xl">
        <DialogHeader
          id={titleId}
          title={title}
          eyebrow={memo?.scopeLabel || scopeLabel}
          onClose={() => setExpanded(false)}
        />
        <DialogBody className="pb-6">{body(true)}</DialogBody>
      </Dialog>
    </section>
  );
};

export default BriefPanel;
